import { useState } from "react";
import { FileText, Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useKnowledgeLibrarySources } from "@/components/admin_dashboard/knowledge-library/useKnowledgeLibrarySources";
import {
  MAX_WORKFLOW_INSTRUCTIONS_CHARS,
  MAX_WORKFLOW_KNOWLEDGE_SOURCES,
  type WorkflowKnowledge,
} from "@/lib/workflows/schema";
import { isPickableKnowledge, normalizeWorkflowKnowledge } from "./workflowKnowledge";

interface WorkflowKnowledgePanelProps {
  businessId: string;
  value: WorkflowKnowledge | undefined;
  onChange: (next: WorkflowKnowledge | undefined) => void;
}

/**
 * "Knowledge for this workflow": instructions and Knowledge items that apply
 * only while the AI acts for this workflow. Business Knowledge stays the
 * baseline; where they disagree, these win.
 */
export default function WorkflowKnowledgePanel({ businessId, value, onChange }: WorkflowKnowledgePanelProps) {
  const sources = useKnowledgeLibrarySources(businessId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const instructions = value?.instructions ?? "";
  const selectedIds = value?.sourceIds ?? [];
  const pickable = (sources.data ?? []).filter(isPickableKnowledge);
  const needle = query.trim().toLocaleLowerCase();
  const visible = pickable.filter((item) =>
    !needle || [item.name, item.content].join(" ").toLocaleLowerCase().includes(needle)
  );
  const selected = selectedIds.map((id) =>
    pickable.find((item) => item.id === id) ?? {
      id,
      name: sources.isLoading ? "Loading…" : "Removed from Knowledge",
    }
  );
  const atLimit = selectedIds.length >= MAX_WORKFLOW_KNOWLEDGE_SOURCES;

  const update = (patch: WorkflowKnowledge) =>
    onChange(normalizeWorkflowKnowledge({ instructions, sourceIds: selectedIds, ...patch }));
  const toggle = (id: string, on: boolean) =>
    update({
      sourceIds: on
        ? [...selectedIds.filter((existing) => existing !== id), id]
        : selectedIds.filter((existing) => existing !== id),
    });

  return (
    <section aria-labelledby="workflow-knowledge-heading" className="space-y-3">
      <div>
        <h3 id="workflow-knowledge-heading" className="text-sm font-semibold text-foreground">
          Knowledge for this workflow
        </h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          The AI always follows your Knowledge. Anything you add here applies only to this workflow, in its AI steps and
          when a customer replies to it. Where the two disagree, this wins.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="workflow-knowledge-instructions" className="text-xs">Instructions</Label>
        <Textarea
          id="workflow-knowledge-instructions"
          value={instructions}
          onChange={(event) => update({ instructions: event.target.value })}
          maxLength={MAX_WORKFLOW_INSTRUCTIONS_CHARS}
          rows={3}
          placeholder="e.g. Keep replies to two sentences. If they mention a problem, apologise and say the team will follow up."
          className="resize-y text-sm"
        />
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs font-medium text-foreground">Knowledge items</span>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Add
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent align="end" className="w-[280px] p-2">
          <div className="relative mb-1.5">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Knowledge…"
              className="h-8 pl-8 text-xs"
              aria-label="Search Knowledge"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {sources.isLoading ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">Loading Knowledge…</p>
            ) : visible.length === 0 ? (
              <p className="px-2 py-3 text-xs leading-5 text-muted-foreground">
                {pickable.length === 0
                  ? "Add a note, FAQ or Markdown file in Knowledge first."
                  : "Nothing matches your search."}
              </p>
            ) : (
              visible.map((item) => {
                const checked = selectedIds.includes(item.id);
                const disabled = !checked && atLimit;
                return (
                  <div
                    key={item.id}
                    role="checkbox"
                    aria-checked={checked}
                    aria-disabled={disabled}
                    tabIndex={disabled ? -1 : 0}
                    className={`flex items-center gap-2 rounded-md px-2 py-2 transition-colors ${
                      disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-muted"
                    }`}
                    onClick={() => !disabled && toggle(item.id, !checked)}
                    onKeyDown={(event) => {
                      if (disabled || (event.key !== "Enter" && event.key !== " ")) return;
                      event.preventDefault();
                      toggle(item.id, !checked);
                    }}
                  >
                    <Checkbox checked={checked} tabIndex={-1} aria-hidden="true" className="pointer-events-none" />
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 truncate text-xs font-medium text-foreground">{item.name}</span>
                  </div>
                );
              })
            )}
          </div>
          {atLimit && (
            <p className="px-2 pt-1.5 text-[11px] text-muted-foreground">
              Up to {MAX_WORKFLOW_KNOWLEDGE_SOURCES} items per workflow.
            </p>
          )}
        </PopoverContent>
      </Popover>

      {selected.length === 0 ? (
        <p className="text-xs text-muted-foreground">None picked.</p>
      ) : (
        <ul className="space-y-1">
          {selected.map((item) => (
            <li
              key={item.id}
              className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5"
            >
              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-xs text-foreground">{item.name}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                aria-label={`Remove ${item.name}`}
                onClick={() => toggle(item.id, false)}
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
